// Encodes calculator inputs into shareable URL query params and back.
// Compact keys keep shared links short enough for social posts.

export interface FemaleFormData {
  ageRange: [number, number];
  race: string[];
  politics: string;
  feminist: string;
  children: string;
  bodyType: string;
  familyStructure: string;
  maritalStatus: string;
  sexualPartnerCount: string;
  educationalLevel: string;
}

export interface MaleShareFormData {
  ageRange: [number, number];
  race: string[];
  heightMin: string;
  incomeMin: string;
  politics: string;
  maritalStatus: string;
  bodyType: string;
  educationalLevel: string;
  employment: string;
}

const NO_PREF = "no-preference";

function setIfSet(params: URLSearchParams, key: string, value: string) {
  if (value && value !== NO_PREF) params.set(key, value);
}

export function encodeFemaleParams(data: FemaleFormData): string {
  const params = new URLSearchParams();
  params.set("age", `${data.ageRange[0]}-${data.ageRange[1]}`);
  if (!data.race.includes("all")) params.set("race", data.race.join(","));
  setIfSet(params, "pol", data.politics);
  setIfSet(params, "fem", data.feminist);
  setIfSet(params, "kids", data.children);
  setIfSet(params, "body", data.bodyType);
  setIfSet(params, "fam", data.familyStructure);
  setIfSet(params, "mar", data.maritalStatus);
  setIfSet(params, "exp", data.sexualPartnerCount);
  setIfSet(params, "edu", data.educationalLevel);
  return params.toString();
}

export function decodeFemaleParams(search: string): FemaleFormData | null {
  const params = new URLSearchParams(search);
  if (!params.has("age")) return null;
  const [min, max] = (params.get("age") || "22-35").split("-").map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return {
    ageRange: [Math.max(18, min), Math.min(100, max)],
    race: params.get("race")?.split(",").filter(Boolean) ?? ["all"],
    politics: params.get("pol") ?? NO_PREF,
    feminist: params.get("fem") ?? NO_PREF,
    children: params.get("kids") ?? NO_PREF,
    bodyType: params.get("body") ?? NO_PREF,
    familyStructure: params.get("fam") ?? NO_PREF,
    maritalStatus: params.get("mar") ?? NO_PREF,
    sexualPartnerCount: params.get("exp") ?? NO_PREF,
    educationalLevel: params.get("edu") ?? NO_PREF,
  };
}

export function encodeMaleParams(data: MaleShareFormData): string {
  const params = new URLSearchParams();
  params.set("age", `${data.ageRange[0]}-${data.ageRange[1]}`);
  if (!data.race.includes("all")) params.set("race", data.race.join(","));
  setIfSet(params, "h", data.heightMin);
  setIfSet(params, "inc", data.incomeMin);
  setIfSet(params, "pol", data.politics);
  setIfSet(params, "mar", data.maritalStatus);
  setIfSet(params, "body", data.bodyType);
  setIfSet(params, "edu", data.educationalLevel);
  setIfSet(params, "emp", data.employment);
  return params.toString();
}

export function decodeMaleParams(search: string): MaleShareFormData | null {
  const params = new URLSearchParams(search);
  if (!params.has("age")) return null;
  const [min, max] = (params.get("age") || "22-40").split("-").map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return {
    ageRange: [Math.max(18, min), Math.min(100, max)],
    race: params.get("race")?.split(",").filter(Boolean) ?? ["all"],
    heightMin: params.get("h") ?? NO_PREF,
    incomeMin: params.get("inc") ?? NO_PREF,
    politics: params.get("pol") ?? NO_PREF,
    maritalStatus: params.get("mar") ?? NO_PREF,
    bodyType: params.get("body") ?? NO_PREF,
    educationalLevel: params.get("edu") ?? NO_PREF,
    employment: params.get("emp") ?? NO_PREF,
  };
}
